import React, {useState, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {PromptVersionBadge} from '../../prompts/PromptVersionBadge';
import {pingPromptCopied} from '../../prompts/promptCopyPing';

/**
 * Bloque de markdown copiable (instrucciones / conocimiento del cliente).
 * Reusa el patrón visual de OfficialPromptsSection (pre.prompt-text + botón copiar
 * con estado "copiado" por 2s).
 *
 * `promptMeta` ({id, version, agencyClientId}) es opcional: cuando el markdown corresponde a
 * un prompt oficial, el copy registra el ping de copiado y `versionStatus` alimenta el chip
 * de versión. Sin esos props (default null) el bloque se comporta exactamente como siempre.
 */
export function CopyableMarkdownBlock({title, description, markdown, promptMeta = null, versionStatus = null, onCopied = null}) {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(() => {
        if (!markdown) return;
        navigator.clipboard.writeText(markdown).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            // Ping de copiado (fire-and-forget, patrón PromptLibraryCard): solo si la
            // copia ocurrió de verdad.
            if (promptMeta && promptMeta.id) {
                pingPromptCopied(promptMeta.id, {
                    version: promptMeta.version,
                    agencyClientId: promptMeta.agencyClientId,
                });
                if (onCopied) onCopied(promptMeta.id, promptMeta.version);
            }
        }).catch(() => {});
    }, [markdown, promptMeta, onCopied]);

    return (
        <div className="ia-assistant-block">
            <header className="ia-assistant-block-header">
                <div className="ia-assistant-block-titles">
                    <h3 className="ia-assistant-block-title">{title}</h3>
                    {description && (
                        <p className="ia-assistant-block-description">{description}</p>
                    )}
                </div>
                <div className="ia-assistant-block-actions">
                    <PromptVersionBadge status={versionStatus}/>
                    <button className="btn -alt -negative" onClick={handleCopy}>
                        {copied ? t('ia_assistant.copied') : t('ia_assistant.copy')}
                    </button>
                </div>
            </header>
            <pre className="prompt-text">{markdown}</pre>
        </div>
    );
}
